import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
} from '@mui/material';
import { Link } from 'react-router-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalculateIcon from '@mui/icons-material/Calculate';
import DescriptionIcon from '@mui/icons-material/Description';
import SecurityIcon from '@mui/icons-material/Security';
import DevicesIcon from '@mui/icons-material/Devices';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const features = [
  {
    icon: <AccessTimeIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Time Tracking',
    description:
      'Track contractor hours with our intuitive timesheet system. Weekly views with configurable week-ending days.',
  },
  {
    icon: <CalculateIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Automatic Calculations',
    description:
      'Instantly calculate gross pay based on hourly rates. No manual math needed.',
  },
  {
    icon: <DescriptionIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: '1099-MISC Generation',
    description:
      'Generate IRS-compliant 1099-MISC forms at tax time. Download ready-to-file PDFs.',
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Bank-Level Security',
    description:
      'Your data is encrypted and secure. TIN/SSN information is protected with industry-standard encryption.',
  },
  {
    icon: <DevicesIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Works Everywhere',
    description:
      'Access SimplyPayroll from any device. Desktop, tablet, or mobile – your payroll data is always available.',
  },
  {
    icon: <SupportAgentIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Dedicated Support',
    description:
      'Get help when you need it. Our support team is here to assist with any questions.',
  },
];

const benefits = [
  'No complex setup required',
  'Perfect for 1099 contractors',
  'IRS-compliant 1099 forms',
  'Unlimited contractors',
  'Secure data encryption',
  'Export reports anytime',
];

export default function LandingPage() {
  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '100vh',
          background:
            'linear-gradient(135deg, #1E23FE 0%, #1B35E9 50%, #0D9E6F 100%)',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          pt: 8,
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `radial-gradient(circle at 20% 80%, rgba(30, 35, 254, 0.15) 0%, transparent 50%),
                             radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)`,
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Chip
                label="Simple 1099 Payroll Tracking"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  fontWeight: 500,
                  mb: 3,
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  color: 'white',
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                  fontWeight: 800,
                  lineHeight: 1.1,
                  mb: 3,
                }}
              >
                Track Hours.
                <br />
                Calculate Pay.
                <br />
                <Box component="span" sx={{ color: '#0CA963' }}>
                  Generate 1099s.
                </Box>
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: 'rgba(255,255,255,0.85)',
                  fontWeight: 400,
                  lineHeight: 1.6,
                  mb: 4,
                  maxWidth: 500,
                }}
              >
                The simplest way to track contractor hours and generate
                tax-ready 1099 forms for your business.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => {
                    window.location.href = `${
                      process.env.REACT_APP_APP_URL || 'https://localhost:6001'
                    }/register`;
                  }}
                  sx={{
                    bgcolor: 'white',
                    color: '#1E23FE',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                    },
                  }}
                >
                  Get Started
                </Button>
                <Button
                  component={Link}
                  to="/features"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.5)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  See Features
                </Button>
              </Stack>
              <Box sx={{ mt: 4 }}>
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(255,255,255,0.6)' }}
                >
                  ✓ Monthly or Yearly billing &nbsp; ✓ Same features on all
                  plans &nbsp; ✓ Cancel anytime
                </Typography>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={5}
              sx={{ display: { xs: 'none', md: 'block' } }}
            >
              {/* Dashboard Preview Illustration */}
              <Box
                sx={{
                  bgcolor: 'white',
                  borderRadius: 4,
                  p: 3,
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
                  transform: 'perspective(1000px) rotateY(-5deg) rotateX(2deg)',
                }}
              >
                <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 2, p: 2, mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'text.secondary', mb: 1 }}
                  >
                    Weekly Timesheet
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, i) => (
                      <Box
                        key={i}
                        sx={{
                          flex: 1,
                          textAlign: 'center',
                          py: 1,
                          bgcolor: i < 5 ? 'primary.main' : 'grey.300',
                          color: i < 5 ? 'white' : 'text.secondary',
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        {day}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Hours This Week
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    40.0 hrs
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Rate
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    $75.00/hr
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderTop: 1,
                    borderColor: 'divider',
                    pt: 1,
                    mt: 1,
                  }}
                >
                  <Typography variant="body1" fontWeight={600}>
                    Gross Pay
                  </Typography>
                  <Typography variant="body1" fontWeight={700} color="primary">
                    $3,000.00
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: 8, bgcolor: '#f8faf8' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
              Why Choose SimplyPayroll?
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto' }}
            >
              Built specifically for small businesses that work with 1099
              contractors. No unnecessary features, just what you need.
            </Typography>
          </Box>
          <Grid container spacing={2} justifyContent="center">
            {benefits.map((benefit, index) => (
              <Grid item xs={6} sm={4} md={2} key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 2,
                    bgcolor: 'white',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  }}
                >
                  <CheckCircleIcon
                    sx={{ color: 'primary.main', fontSize: 20 }}
                  />
                  <Typography variant="body2" fontWeight={500}>
                    {benefit}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 12 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="Features"
              sx={{ mb: 2 }}
              color="primary"
              variant="outlined"
            />
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
              Everything You Need
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto' }}
            >
              SimplyPayroll gives you all the tools to manage contractor
              payments and stay compliant with IRS requirements.
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works */}
      <Box sx={{ py: 12, bgcolor: '#f8faf8' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="How It Works"
              sx={{ mb: 2 }}
              color="primary"
              variant="outlined"
            />
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
              Get Started in 3 Simple Steps
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {[
              {
                step: '1',
                title: 'Add Contractors',
                description:
                  'Enter your 1099 contractors with their rates and tax information.',
              },
              {
                step: '2',
                title: 'Track Time',
                description:
                  'Log hours weekly. Our system automatically calculates gross pay.',
              },
              {
                step: '3',
                title: 'Generate 1099s',
                description:
                  'At tax time, generate and download 1099-MISC forms with one click.',
              },
            ].map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background:
                        'linear-gradient(135deg, #1E23FE 0%, #0D9E6F 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                      boxShadow: '0 8px 24px rgba(30, 35, 254, 0.3)',
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{ color: 'white', fontWeight: 700 }}
                    >
                      {item.step}
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {item.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: 12,
          background: 'linear-gradient(135deg, #1E23FE 0%, #0D9E6F 100%)',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            sx={{ color: 'white', fontWeight: 700, mb: 2 }}
          >
            Ready to Simplify Your Payroll?
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: 'rgba(255,255,255,0.85)', mb: 4 }}
          >
            Join thousands of small businesses tracking contractor hours with
            SimplyPayroll.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              window.location.href = `${
                process.env.REACT_APP_APP_URL || 'https://localhost:6001'
              }/register`;
            }}
            sx={{
              bgcolor: 'white',
              color: '#1E23FE',
              px: 6,
              py: 2,
              fontSize: '1.125rem',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.9)',
              },
            }}
          >
            Get Started Now
          </Button>
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.6)', mt: 2 }}
          >
            Monthly and yearly plans available • Same great features
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
